// frontend/src/pages/admin/ViewAnalyticsPage.tsx
import { useState, useCallback } from 'react'; // Added useEffect
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { DatePickerWithRange } from '@/components/ui/date-range-picker';
import { type DateRange } from "react-day-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart3, Users, Eye, Clock, ExternalLink, Percent, Filter, Loader2, AlertCircle, LayoutDashboard } from 'lucide-react';
import { Label } from '@/components/ui/label';

// Import your API service functions
import {
  getTrafficOverviewKpis,
  getTrafficOverTime,
  getTopPages,
  getDemographicsData
} from '@/api/analyticsService';

// Import your response types
import {
  type TrafficOverviewKpis,
  type TrafficOverTimeResponse,
  type TopPagesResponse,
  type DemographicsResponse,
  type KpiValue // Assuming KpiValue is part of your analyticsTypes
} from '@/types/analyticsTypes'; // Ensure this path is correct

// Placeholder component for charts - you'll replace this with actual chart components
const PlaceholderChart = ({ title, height = "h-64", children }: { title: string, height?: string, children?: React.ReactNode }) => (
  <Card className="bg-card border-border/30">
    <CardHeader>
      <CardTitle className="text-lg font-medium text-foreground">{title}</CardTitle>
    </CardHeader>
    <CardContent>
      <div className={`flex items-center justify-center ${height} bg-muted/30 dark:bg-muted/10 rounded-md border border-dashed border-border/50`}>
        {children || <p className="text-muted-foreground">Chart data for "{title}" will appear here.</p>}
      </div>
    </CardContent>
  </Card>
);

// Define an interface for your KPI data objects if not already globally defined
interface KpiDisplayData {
    id: string;
    title: string;
    value: string;
    trend: string;
    icon: React.FC<any>; // or specific LucideProps
    iconColor: string;
    previous_value?: string;
}

const initialKpiStructure: KpiDisplayData[] = [
  { id: 'total_visits', title: 'Total Visits', value: '0', trend: 'N/A', icon: Users, iconColor: 'text-primary' },
  { id: 'unique_visitors', title: 'Unique Visitors', value: '0', trend: 'N/A', icon: Users, iconColor: 'text-accent' },
  { id: 'page_views', title: 'Page Views', value: '0', trend: 'N/A', icon: Eye, iconColor: 'text-primary' },
  { id: 'avg_session_duration', title: 'Avg. Session Duration', value: '0m 0s', trend: 'N/A', icon: Clock, iconColor: 'text-vedasMaroon-600 dark:text-vedasMaroon-500' },
  { id: 'engagement_rate', title: 'Engagement Rate', value: '0%', trend: 'N/A', icon: Percent, iconColor: 'text-green-600 dark:text-green-500' },
  // You might want to fetch top article views separately or as part of another call
  // { id: 'topArticle', title: 'Top Article Views', value: '0', trend: 'N/A', icon: FileText, iconColor: 'text-primary' },
];


type DataSourceType = "all" | "ga4" | "internal"; // "all" and "internal" are not used by current backend GA service
type UserSegmentType = "all-users" | "new-users" | "returning-users" | "registered"; // Not used by current backend GA service

const ViewAnalyticsPage = () => {
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [selectedDataSource, setSelectedDataSource] = useState<DataSourceType>("ga4"); // Default to ga4
  const [selectedUserSegment, setSelectedUserSegment] = useState<UserSegmentType>("all-users");

  // State for fetched data
  const [kpiData, setKpiData] = useState<TrafficOverviewKpis | null>(null);
  const [trafficOverTimeData, setTrafficOverTimeData] = useState<TrafficOverTimeResponse | null>(null);
  const [topPagesData, setTopPagesData] = useState<TopPagesResponse | null>(null);
  const [demographicsData, setDemographicsData] = useState<DemographicsResponse | null>(null);
  const [selectedDemographicsDimension, setSelectedDemographicsDimension] = useState<'country' | 'deviceCategory'>('country');


  const [isLoadingData, setIsLoadingData] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

   const mapKpiDataToDisplay = (apiKpis: TrafficOverviewKpis | null): KpiDisplayData[] => {
    if (!apiKpis) return initialKpiStructure.map(k => ({...k, value: 'N/A', trend: 'N/A'}));

    return initialKpiStructure.map(displayKpi => {
        const key = displayKpi.id as keyof TrafficOverviewKpis;
        if (apiKpis[key]) {
            const apiKpiValue = apiKpis[key] as KpiValue;
            return {
                ...displayKpi,
                value: apiKpiValue.value || '0',
                trend: apiKpiValue.trend || '+0%',
                // --- FIX: Coalesce null to undefined to match the KpiDisplayData type ---
                previous_value: apiKpiValue.previous_value ?? undefined
            };
        }
        return {...displayKpi, value: 'N/A', trend: 'N/A'};
    });
  };


  const fetchAnalyticsData = useCallback(async () => {
    if (!dateRange || !dateRange.from || !dateRange.to) {
      setFetchError("Please select a valid date range.");
      return;
    }

    setIsLoadingData(true);
    setFiltersApplied(true);
    setFetchError(null);

    // Reset previous data
    setKpiData(null);
    setTrafficOverTimeData(null);
    setTopPagesData(null);
    setDemographicsData(null);

    try {
      // For now, we assume selectedDataSource is "ga4" if active.
      // UserSegment is not directly passed to these GA4 specific calls yet.
      const kpis = await getTrafficOverviewKpis(dateRange);
      setKpiData(kpis);

      const trafficTime = await getTrafficOverTime(dateRange);
      setTrafficOverTimeData(trafficTime);

      const topPages = await getTopPages(dateRange, 10); // Fetch top 10 pages
      setTopPagesData(topPages);

      const demographics = await getDemographicsData(dateRange, selectedDemographicsDimension);
      setDemographicsData(demographics);

    } catch (err: any) {
      console.error("Failed to fetch analytics data:", err);
      const errorDetail = err.response?.data?.detail || err.message || "An error occurred while fetching analytics data.";
      setFetchError(errorDetail);
    } finally {
      setIsLoadingData(false);
    }
  }, [dateRange, selectedDataSource, selectedUserSegment, selectedDemographicsDimension]); // Add selectedDemographicsDimension

  const handleApplyFilters = () => {
    fetchAnalyticsData();
  };

  const displayKpis = mapKpiDataToDisplay(kpiData);

  return (
    <div className="container mx-auto px-4 py-8 md:py-12 space-y-10 text-foreground bg-background">
      {/* Header */}
      <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-border/60 pb-6 relative">
        <div className="absolute bottom-0 left-0 w-1/3 h-px bg-gradient-to-r from-primary/70 via-accent/50 to-transparent"></div>
        <div className="flex items-center">
          <BarChart3 className="h-10 w-10 text-primary mr-4 flex-shrink-0" />
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Site <span className="text-gradient-maroon-to-gold">Analytics</span>
            </h1>
            <p className="mt-1 text-muted-foreground">
              Detailed insights and performance metrics for Vedas Trading.
            </p>
          </div>
        </div>
        <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10 hover:text-primary transition-colors self-start sm:self-center w-full sm:w-auto">
              <Link to="/admin/dashboard">
                <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
              </Link>
            </Button>
      </header>

      {/* Filters Section */}
      <section className="p-4 md:p-6 bg-muted/50 dark:bg-muted/10 rounded-lg border border-border/30 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          <div>
            <Label htmlFor="date-range" className="block text-sm font-medium text-foreground mb-1.5">Date Range</Label>
            <DatePickerWithRange date={dateRange} onDateChange={setDateRange} className="w-full" placeholder="Select Date Range"/>
          {/* <p className="text-xs text-muted-foreground mt-1">Select a date range.</p> */}
          </div>
          {/* Data Source and User Segment filters are UI-only for now as backend GA service doesn't use them */}
          <div>
            <Label htmlFor="data-source" className="block text-sm font-medium text-foreground mb-1.5">Data Source</Label>
            <Select value={selectedDataSource} onValueChange={(value: DataSourceType) => setSelectedDataSource(value)} disabled>
              <SelectTrigger id="data-source" className="w-full bg-input border-border focus:border-primary focus:ring-primary text-foreground">
                <SelectValue placeholder="Select source" />
              </SelectTrigger>
              <SelectContent className="bg-popover text-popover-foreground border-border">
                <SelectItem value="ga4">Google Analytics 4</SelectItem>
                {/* <SelectItem value="all" disabled>All Sources (Future)</SelectItem>
                <SelectItem value="internal" disabled>Internal Tracking (Future)</SelectItem> */}
              </SelectContent>
            </Select>
             {/* <p className="text-xs text-muted-foreground mt-1">Currently supports GA4 only.</p> */}
          </div>
          <div>
            <Label htmlFor="segment" className="block text-sm font-medium text-foreground mb-1.5">User Segment</Label>
            <Select value={selectedUserSegment} onValueChange={(value: UserSegmentType) => setSelectedUserSegment(value)} disabled>
              <SelectTrigger id="segment" className="w-full bg-input border-border focus:border-primary focus:ring-primary text-foreground">
                <SelectValue placeholder="Select segment" />
              </SelectTrigger>
              <SelectContent className="bg-popover text-popover-foreground border-border">
                <SelectItem value="all-users">All Users</SelectItem>
                {/* <SelectItem value="new-users" disabled>New Users (Future)</SelectItem>
                <SelectItem value="returning-users" disabled>Returning Users (Future)</SelectItem>
                <SelectItem value="registered" disabled>Registered Users (Future)</SelectItem> */}
              </SelectContent>
            </Select>
            {/* <p className="text-xs text-muted-foreground mt-1">Future enhancement.</p> */}
          </div>
          <Button 
            onClick={handleApplyFilters} 
            className="w-full md:w-auto !bg-gradient-to-r !from-primary !to-accent hover:!from-primary/90 hover:!to-accent/90 text-primary-foreground"
            disabled={isLoadingData || !dateRange?.from || !dateRange?.to}
          >
            {isLoadingData ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Filter className="mr-2 h-4 w-4" />}
            Apply Filters
          </Button>
        </div>
      </section>

      {fetchError && (
        <div className="p-4 my-4 bg-destructive/10 text-destructive border border-destructive/30 rounded-md flex items-center">
            <AlertCircle className="h-5 w-5 mr-3 flex-shrink-0" />
            <div>
                <p className="font-semibold">Error Fetching Analytics</p>
                <p className="text-sm">{fetchError}</p>
            </div>
        </div>
      )}

      {/* KPI Overview Section */}
      <section>
        <h2 className="text-2xl font-semibold mb-6 text-foreground">Key Performance Indicators</h2>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"> {/* Adjusted to 5 for initialKpiStructure */}
          {displayKpis.map((kpi) => {
            const KpiIcon = kpi.icon;
            const isNegativeTrend = kpi.trend?.startsWith('-');
            const trendColor = kpi.trend === 'N/A' ? 'text-muted-foreground' : (isNegativeTrend ? 'text-destructive' : 'text-green-600 dark:text-green-500');
            return (
              <Card key={kpi.id} className="group bg-card text-card-foreground shadow-lg hover:shadow-xl dark:hover:shadow-primary/10 transition-all duration-300 ease-in-out border border-border/30 hover:border-primary/30 relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-accent/80 via-primary/80 to-accent/80 scale-x-0 group-hover:scale-x-100 transition-transform duration-400 ease-out origin-center"></div>
                <div className="relative z-10 p-4 md:p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-muted-foreground group-hover:text-foreground transition-colors">{kpi.title}</h3>
                    <KpiIcon className={`h-5 w-5 ${kpi.iconColor} group-hover:scale-110 transition-transform`} />
                  </div>
                  <p className="text-2xl md:text-3xl font-bold text-foreground group-hover:text-primary transition-colors">
                    {isLoadingData && filtersApplied ? <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /> : kpi.value}
                  </p>
                  {kpi.trend !== 'N/A' && (
                     <p className={`text-xs mt-1 ${isLoadingData && filtersApplied ? 'text-muted-foreground' : trendColor}`}>
                       {isLoadingData && filtersApplied ? 'Loading...' : `${kpi.trend} vs last period`}
                     </p>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
        {!filtersApplied && !isLoadingData && !fetchError && (
            <div className="mt-6 p-4 bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/30 rounded-md text-center">
                Select a date range and click "Apply Filters" to load analytics data.
            </div>
        )}
      </section>

      {/* Charts Section */}
      <section className="space-y-8">
        <h2 className="text-2xl font-semibold text-foreground">Visual Reports</h2>
        {isLoadingData && filtersApplied ? ( /* Show loaders if actively loading after filters applied */
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {[...Array(2)].map((_, i) => (
                    <PlaceholderChart key={`loader-chart-${i}`} title="Loading Chart...">
                        <Loader2 className="h-10 w-10 animate-spin text-primary"/>
                    </PlaceholderChart>
                ))}
            </div>
        ) : filtersApplied && !fetchError ? ( /* Show charts if filters applied and no error */
            <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <PlaceholderChart title="Traffic Over Time (Visits & Page Views)">
                    {trafficOverTimeData ?  <p className='text-sm text-muted-foreground'>Replace with actual Traffic Over Time chart. <br/>Data Points: {trafficOverTimeData.series.length}</p> : <p className='text-sm text-muted-foreground'>No data for Traffic Over Time.</p>}
                </PlaceholderChart>
                <PlaceholderChart title="User Demographics">
                   <div className='w-full text-center'>
                     <Select value={selectedDemographicsDimension} onValueChange={(dim) => setSelectedDemographicsDimension(dim as 'country' | 'deviceCategory')}>
                        <SelectTrigger className="w-[180px] mx-auto mb-2 bg-input border-border text-foreground">
                            <SelectValue placeholder="Dimension" />
                        </SelectTrigger>
                        <SelectContent className="bg-popover border-border text-popover-foreground">
                            <SelectItem value="country">By Country</SelectItem>
                            <SelectItem value="deviceCategory">By Device</SelectItem>
                        </SelectContent>
                    </Select>
                    {demographicsData ? <p className='text-sm text-muted-foreground'>Replace with actual Demographics chart. <br/>Dimension: {demographicsData.dimension_name}, Data Points: {demographicsData.data.length}</p> : <p className='text-sm text-muted-foreground'>No data for Demographics.</p>}
                   </div>
                </PlaceholderChart>
            </div>
            {/* Add more chart placeholders as needed */}
            </>
        ) : ( /* Initial message before filters applied or if error occurred */
            <div className="p-6 bg-muted/50 dark:bg-muted/10 rounded-lg border border-dashed border-border/30 text-center text-muted-foreground">
                {!fetchError && "Apply filters to view visual reports."}
                {fetchError && "Could not load chart data due to an error."}
            </div>
        )}
      </section>

      {/* Data Tables Section - e.g., Top Pages */}
        <section className="space-y-8">
            <h2 className="text-2xl font-semibold text-foreground">Detailed Data</h2>
            {isLoadingData && filtersApplied ? (
                 <PlaceholderChart title="Loading Top Pages...">
                    <Loader2 className="h-10 w-10 animate-spin text-primary"/>
                </PlaceholderChart>
            ) : filtersApplied && !fetchError && topPagesData ? (
                <Card className="bg-card border-border/30">
                    <CardHeader>
                        <CardTitle className="text-lg font-medium text-foreground">Top Performing Pages</CardTitle>
                        <CardDescription className="text-muted-foreground">Based on page views in the selected period.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        {topPagesData.pages.length > 0 ? (
                            <ul className='list-disc pl-5 space-y-1 text-muted-foreground'>
                                {topPagesData.pages.map(page => (
                                    <li key={page.page_path}>
                                       <span className='font-medium text-foreground'>{page.page_title || page.page_path}</span>: {page.page_views.toLocaleString()} views
                                    </li>
                                ))}
                            </ul>
                        ) : <p className="text-muted-foreground">No top pages data available for this period.</p>}
                    </CardContent>
                </Card>
            ) : (
                <div className="p-6 bg-muted/50 dark:bg-muted/10 rounded-lg border border-dashed border-border/30 text-center text-muted-foreground">
                     {!fetchError && "Apply filters to view detailed data tables."}
                     {fetchError && "Could not load table data due to an error."}
                </div>
            )}
        </section>


      {/* Call to Action or Further Info */}
      <section className="mt-12 py-8 border-t border-border/30 text-center">
        <p className="text-muted-foreground mb-4">
          Need more detailed reports or custom analytics integrations?
        </p>
        <Button variant="outline" className="border-primary text-primary hover:bg-primary/10 hover:text-primary transition-colors" asChild>
          <Link to="/contact?subject=AnalyticsInquiry">
            Contact Support <ExternalLink className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </section>
    </div>
  );
};

export default ViewAnalyticsPage;